import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Share } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AppleButton } from "@/components/ui/apple-button";
import { AppleCard } from "@/components/ui/apple-card";
import { SharePlaceDialog } from "@/components/places/SharePlaceDialog";
import { useTranslation } from "react-i18next";

interface Place {
  id: number;
  name: string;
  companies: {
    name: string;
  };
  type?: string;
  company_id: number;
  description?: string;
}

const Places = () => {
  const [places, setPlaces] = useState<Place[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    fetchPlaces();
  }, []);

  const fetchPlaces = async () => {
    try {
      const { data, error } = await supabase
        .from("places")
        .select("*, companies(name)")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setPlaces(data || []);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error fetching places",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('places.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('places.description')}
          </p>
        </div>
        <AppleButton 
          onClick={() => navigate("/places/new")}
          className="w-full sm:w-auto flex items-center justify-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('places.actions.add')}
        </AppleButton>
      </div>

      {places.map((place) => (
        <AppleCard key={place.id}>
          <div className="flex items-center justify-between">
            <div 
              className="cursor-pointer"
              onClick={() => navigate(`/places/${place.id}`)}
            >
              <h2 className="text-lg font-medium">{place.name}</h2>
              <div className="flex items-center space-x-2 mt-1">
                <p className="text-sm text-gray-500">{place.companies?.name}</p>
                {place.type && (
                  <>
                    <span className="text-gray-300">•</span>
                    <p className="text-sm text-gray-500">{place.type}</p>
                  </>
                )}
              </div>
            </div>
            <SharePlaceDialog 
              placeId={place.id} 
              onShare={fetchPlaces}
            >
              <AppleButton 
                variant="secondary" 
                size="sm"
                className="inline-flex items-center space-x-2"
              >
                <Share className="h-4 w-4" />
                <span>{t('places.actions.share')}</span>
              </AppleButton>
            </SharePlaceDialog>
          </div>
        </AppleCard>
      ))}

      {!loading && places.length === 0 && (
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">{t('places.empty')}</p>
          <AppleButton
            variant="secondary"
            size="sm"
            onClick={() => navigate("/places/new")}
            className="mt-4 inline-flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>{t('places.actions.createFirst')}</span>
          </AppleButton>
        </div>
      )}
    </div>
  );
};

export default Places;